// Estructura única de la exportación de Reportes Recepción (PDF y Excel).
//
// POR QUÉ UNA SOLA ESTRUCTURA
// El PDF y el Excel tenían cada uno su propia lista de tablas escrita a mano, y los dos se
// quedaron atrás cuando la pantalla sumó gráficos (resultado de la atención, tendencia por tipo,
// trámites iniciados, turnos por día, conversión mensual, la pestaña histórica). Acá se arma una
// vez, pestaña por pestaña y en el mismo orden que la pantalla, y los dos documentos la recorren:
// el PDF dibuja gráfico + tabla por sección, el Excel una hoja por sección.
//
// Funciones puras, sin DOM ni Chart.js: los gráficos se describen como configuración de Chart.js
// y los dibuja exportacionGrafico.js recién al armar el PDF.
import { COLOR, UMBRALES } from './tokens';
import { calcularAcciones, calcularInsights, conversionGlobal } from './selectors';
import {
    calcularBloquesHorarios, calcularPasosEmbudo, diasConTurnos, etiquetaMes, filasResultadoAtencion, promedioPorDia,
    seriesTendenciaPorTipo,
} from './calculosReportes';

export const PESTANAS_EXPORTACION = ['general', 'ocupacion', 'resultados', 'tendencias', 'operador', 'historicas'];

const NOMBRES_DIA = ['Domingo', 'Lunes', 'Martes', 'Miércoles', 'Jueves', 'Viernes', 'Sábado'];
const DIAS_ORDEN = [1, 2, 3, 4, 5, 6, 0];

const lista = (valor) => (Array.isArray(valor) ? valor : []);

/** Redondeo a 1 decimal; null si no hay número. */
const r1 = (valor) => {
    if (valor === null || valor === undefined || valor === '') return null;
    const numero = Number(valor);
    return Number.isNaN(numero) ? null : Math.round(numero * 10) / 10;
};

const pctDe = (parte, total) => (total > 0 ? r1((parte / total) * 100) : null);

const etiquetaEstado = (estado) => String(estado || '').replaceAll('_', ' ');

const fechaCorta = (iso) => {
    const partes = String(iso || '').substring(0, 10).split('-');
    return partes.length === 3 ? `${partes[2]}/${partes[1]}/${partes[0]}` : String(iso || '');
};

// ── Gráficos (configuración de Chart.js) ─────────────────────────────────────

const FUENTE_GRAFICO = { family: 'Helvetica, Arial, sans-serif', size: 15 };

const opcionesBase = (extra = {}) => ({
    font: FUENTE_GRAFICO,
    layout: { padding: 16 },
    plugins: {
        legend: { display: true, position: 'bottom', labels: { font: FUENTE_GRAFICO, color: COLOR.negro, boxWidth: 14 } },
        ...(extra.plugins || {}),
    },
    scales: extra.scales,
    indexAxis: extra.indexAxis,
    cutout: extra.cutout,
});

const ejes = ({ apilado = false, horizontal = false, sufijo = '' } = {}) => {
    const valor = {
        stacked: apilado, beginAtZero: true,
        grid: { color: COLOR.gris }, ticks: { font: FUENTE_GRAFICO, color: COLOR.grisTexto, callback: (v) => `${v}${sufijo}` },
    };
    const categoria = { stacked: apilado, grid: { display: false }, ticks: { font: FUENTE_GRAFICO, color: COLOR.grisTexto, autoSkip: true, maxRotation: 45 } };
    return horizontal ? { x: valor, y: categoria } : { x: categoria, y: valor };
};

// Las etiquetas largas (motivos de no avance de texto libre, nombres de canal) el eje las corta
// por el principio. En el gráfico se recortan con "…"; la tabla de abajo muestra el texto entero.
const LARGO_MAXIMO_ETIQUETA = 42;
const recortarEtiqueta = (texto) => {
    const valor = String(texto ?? '');
    return valor.length > LARGO_MAXIMO_ETIQUETA ? `${valor.slice(0, LARGO_MAXIMO_ETIQUETA - 1)}…` : valor;
};

const graficoBarras = (etiquetas, datasets, { apilado = false, horizontal = false, sufijo = '', leyenda = true, alto = 440 } = {}) => ({
    tipo: 'bar',
    ancho: 1000,
    alto,
    data: { labels: etiquetas.map(recortarEtiqueta), datasets: datasets.map((d) => ({ borderRadius: 3, ...d })) },
    options: opcionesBase({
        indexAxis: horizontal ? 'y' : 'x',
        scales: ejes({ apilado, horizontal, sufijo }),
        plugins: { legend: { display: leyenda, position: 'bottom', labels: { font: FUENTE_GRAFICO, boxWidth: 14 } } },
    }),
});

const graficoLineas = (etiquetas, datasets, { sufijo = '' } = {}) => ({
    tipo: 'line',
    ancho: 1000,
    alto: 420,
    data: {
        labels: etiquetas,
        datasets: datasets.map((d) => ({ borderWidth: 3, pointRadius: 4, tension: 0.25, fill: false, ...d })),
    },
    options: opcionesBase({ scales: ejes({ sufijo }) }),
});

const graficoDona = (etiquetas, valores, colores) => ({
    tipo: 'doughnut',
    ancho: 1000,
    alto: 420,
    data: { labels: etiquetas, datasets: [{ data: valores, backgroundColor: colores, borderColor: '#fff', borderWidth: 3 }] },
    options: opcionesBase({
        cutout: '58%',
        plugins: { legend: { display: true, position: 'right', labels: { font: FUENTE_GRAFICO, boxWidth: 16, padding: 14 } } },
    }),
});

/** Alto proporcional para barras horizontales, así las etiquetas largas no se pisan. */
const altoHorizontal = (cantidad) => Math.min(900, Math.max(320, 70 + cantidad * 42));

const colorEspera = (minutos) => {
    if (minutos == null) return COLOR.grisFrio;
    if (minutos < 0) return COLOR.grisFrio;
    if (minutos <= UMBRALES.esperaMin.bueno) return COLOR.verde;
    if (minutos <= UMBRALES.esperaMin.alerta) return COLOR.ambar;
    return COLOR.rojo;
};

const COLOR_ESTADO_TURNO = {
    FINALIZADO: COLOR.verde,
    ASISTIO: COLOR.verdeClaro,
    NO_ASISTIO: COLOR.rojo,
    CANCELADO: COLOR.grisFrio,
};

// ── Secciones ────────────────────────────────────────────────────────────────

/** @returns {{id, titulo, hoja, nota?, vacio?, columnas, filas, grafico?}} */
const seccion = (datos) => ({ nota: null, vacio: null, grafico: null, ...datos });

function seccionResultadoAtencion(id, hoja, data, nota) {
    const filas = filasResultadoAtencion(lista(data));
    return seccion({
        id, hoja, nota,
        titulo: 'Resultado de la atención',
        columnas: ['Resultado', 'Atenciones', '% del total'],
        filas: filas.map((f) => [f.resultadoLabel || f.resultado, f.cantidad, r1(f.porcentaje)]),
        grafico: filas.length
            ? graficoDona(filas.map((f) => f.resultadoLabel || f.resultado), filas.map((f) => f.cantidad), filas.map((f) => f.color))
            : null,
    });
}

function seccionMotivosNoAvance(id, hoja, data, nota) {
    const filas = [...lista(data)].sort((a, b) => (b.cantidad || 0) - (a.cantidad || 0));
    return seccion({
        id, hoja, nota,
        titulo: 'Motivos de no avance',
        columnas: ['Motivo', 'Cantidad', '% del total'],
        filas: filas.map((f) => [f.motivoLabel || f.motivo, f.cantidad, r1(f.porcentaje)]),
        grafico: filas.length
            ? graficoBarras(filas.map((f) => f.motivoLabel || f.motivo),
                [{ label: 'Atenciones', data: filas.map((f) => f.cantidad), backgroundColor: COLOR.azul }],
                { horizontal: true, leyenda: false, alto: altoHorizontal(filas.length) })
            : null,
    });
}

function pestanaGeneral(c) {
    const totalEstados = lista(c.porEstado).reduce((acc, e) => acc + (e.cantidad || 0), 0);
    const estados = [...lista(c.porEstado)].sort((a, b) => (b.cantidad || 0) - (a.cantidad || 0));
    const canales = [...lista(c.ausentismoPorCanal)].sort((a, b) => (b.total || 0) - (a.total || 0));
    const totalCanales = canales.reduce((acc, f) => acc + (f.total || 0), 0);
    // Barra y porcentaje sobre la misma base: los turnos en que la persona podía abrir un trámite
    // (totalTurnos de /conversion-por-tipo: se presentó, sin cobros ni quienes vinieron por un trámite que
    // ya tenían en curso). Antes la barra salía de /por-tipo (todos los agendados) y el % de los presentados,
    // así que "16 de 114" se leía como 31%.
    const volumen = lista(c.conversionPorTipo).map((x) => ({
        tipo: c.formatearTipo(x.tipoTramite),
        turnos: x.totalTurnos || 0,
        conTramite: x.conTramite || 0,
        tasa: x.tasaConversion ?? 0,
    })).sort((a, b) => b.turnos - a.turnos);
    const iniciados = [...lista(c.tramitesIniciadosPorTipo)].sort((a, b) => (b.cantidad || 0) - (a.cantidad || 0));
    const acciones = calcularAcciones({
        resumen: c.resumen, porHorario: lista(c.porHorario), dias: diasConTurnos(c.porDia),
        conversionPorTipo: lista(c.conversionPorTipo), capacidad: c.capacidad,
    });

    return {
        id: 'general',
        titulo: 'General',
        secciones: [
            seccion({
                id: 'estados', hoja: 'Estados de turno', titulo: 'Estados de turno',
                nota: 'Solo turnos con resultado (finalizados, asistió, no asistió y cancelados): los que siguen abiertos todavía no aparecen. El % es sobre esos turnos; la Asistencia del encabezado va sobre otra base (turnos que vinieron o faltaron, sin cancelados).',
                columnas: ['Estado', 'Turnos', '% del total'],
                filas: estados.map((e) => [etiquetaEstado(e.estado), e.cantidad, r1(e.porcentaje) ?? pctDe(e.cantidad, totalEstados)]),
                grafico: estados.length
                    ? graficoBarras(estados.map((e) => etiquetaEstado(e.estado)),
                        [{ label: 'Turnos', data: estados.map((e) => e.cantidad), backgroundColor: estados.map((e) => COLOR_ESTADO_TURNO[e.estado] || COLOR.azulClaro) }],
                        { leyenda: false })
                    : null,
            }),
            seccion({
                id: 'canalOrigen', hoja: 'Canal de origen', titulo: 'Canal de origen',
                nota: 'De dónde vinieron los turnos del período (referencia cargada en el turno). Ausentismo = no asistió ÷ resueltos (vinieron o faltaron): la misma base del encabezado.',
                columnas: ['Canal', 'Turnos', 'Resueltos', 'No asistió', 'Ausentismo (%)', '% del total'],
                filas: canales.map((f) => [f.nombreCanal, f.total, f.resueltos ?? f.total, f.noAsistio, r1(f.tasaAusentismo),
                    r1(f.porcentaje) ?? pctDe(f.total, totalCanales)]),
            }),
            seccion({
                id: 'volumenConversion', hoja: 'Volumen y conversión', titulo: 'Volumen y conversión por tipo de trámite',
                nota: 'Por tipo de turno: cuántas atenciones se finalizaron y en cuántas se abrió un trámite. No coincide con "Trámites iniciados por tipo", que agrupa por el módulo del trámite que se abrió.',
                columnas: ['Tipo de trámite', 'Atenciones', 'Con trámite abierto', 'Conversión (%)'],
                filas: volumen.map((v) => [v.tipo, v.turnos, v.conTramite, r1(v.tasa)]),
                grafico: volumen.length
                    ? graficoBarras(volumen.map((v) => v.tipo), [
                        { label: 'Atenciones', data: volumen.map((v) => v.turnos), backgroundColor: COLOR.azulClaro },
                        { label: 'Con trámite abierto', data: volumen.map((v) => v.conTramite), backgroundColor: COLOR.verde },
                    ])
                    : null,
            }),
            seccion({
                id: 'tramitesIniciados', hoja: 'Trámites iniciados', titulo: 'Trámites iniciados por tipo',
                columnas: ['Tipo de trámite', 'Trámites', '% del total'],
                filas: iniciados.map((f) => [c.formatearTipo(f.nombreTipoTramite), f.cantidad, r1(f.porcentaje)]),
                grafico: iniciados.length
                    ? graficoBarras(iniciados.map((f) => c.formatearTipo(f.nombreTipoTramite)),
                        [{ label: 'Trámites', data: iniciados.map((f) => f.cantidad), backgroundColor: COLOR.azul }], { leyenda: false })
                    : null,
            }),
            seccion({
                id: 'acciones', hoja: 'Acciones sugeridas', titulo: 'Acciones sugeridas',
                vacio: 'No hay acciones sugeridas: los indicadores del período están dentro de la meta.',
                columnas: ['Acción', 'Detalle', 'Regla que la dispara'],
                filas: acciones.map((a) => [a.title, a.text, a.rule]),
            }),
        ],
    };
}

function pestanaOcupacion(c) {
    const franjas = [...lista(c.porHorario)].sort((a, b) => String(a.horario).localeCompare(String(b.horario)));
    // La capacidad es por franja y por día: los totales del período se llevan a promedio por día.
    const diasPeriodo = diasConTurnos(c.porDia);
    const bloques = calcularBloquesHorarios(franjas, lista(c.tiempoEspera), c.capacidad, diasPeriodo);
    const espera = [...lista(c.tiempoEspera)].sort((a, b) => String(a.horario).localeCompare(String(b.horario)));

    // Mapa de calor como tabla: una fila por hora, una columna por día con datos (mismo agrupado
    // por hora que HeatmapDemanda).
    const porCelda = new Map();
    const horas = new Set();
    const dias = new Set();
    lista(c.heatmapOcupacion).forEach((celda) => {
        const hora = String(celda.horario).split(':')[0];
        horas.add(hora);
        dias.add(celda.diaSemana);
        const clave = `${celda.diaSemana}-${hora}`;
        porCelda.set(clave, (porCelda.get(clave) || 0) + (celda.total || 0));
    });
    const diasConDatos = DIAS_ORDEN.filter((d) => dias.has(d));
    const horasOrdenadas = [...horas].sort();

    return {
        id: 'ocupacion',
        titulo: 'Ocupación',
        secciones: [
            seccion({
                id: 'demandaFranja', hoja: 'Demanda por franja', titulo: 'Demanda por franja horaria',
                nota: c.capacidad
                    ? `Capacidad configurada en el turnero: ${c.capacidad} turno(s) por franja y por día. El gráfico muestra el promedio de turnos por día (total del período ÷ ${diasPeriodo} día(s) con turnos), en la misma unidad que la capacidad. "Agendó el bot" / "Cargado en recepción" es cómo se creó el turno, no cómo llegó la persona.`
                    : null,
                columnas: ['Franja', 'Turnos del período', 'Promedio por día', 'Agendó el bot', 'Cargado en recepción', 'Atendidos', 'Sobreturnos'],
                filas: franjas.map((f) => [f.horario, f.total, r1(promedioPorDia(f.total, diasPeriodo)), f.bot, f.manual, f.atendidos, f.sobreturnos]),
                grafico: franjas.length
                    ? graficoBarras(franjas.map((f) => f.horario), [
                        { label: 'Agendó el bot', data: franjas.map((f) => r1(promedioPorDia(f.bot || 0, diasPeriodo)) ?? 0), backgroundColor: COLOR.azul, stack: 'demanda' },
                        { label: 'Cargado en recepción', data: franjas.map((f) => r1(promedioPorDia(f.manual || 0, diasPeriodo)) ?? 0), backgroundColor: COLOR.azulClaro, stack: 'demanda' },
                        ...(c.capacidad ? [{
                            type: 'line', label: 'Capacidad', data: franjas.map(() => c.capacidad),
                            borderColor: COLOR.rojo, borderDash: [6, 6], borderWidth: 2, pointRadius: 0, fill: false,
                        }] : []),
                    ], { apilado: true })
                    : null,
            }),
            seccion({
                id: 'bloques', hoja: 'Resumen por bloque', titulo: 'Resumen por bloque de 2 horas',
                nota: 'Ocupación = turnos promedio por día del bloque sobre la capacidad de sus franjas con datos en un día (verde hasta 100%, ámbar hasta 160%, rojo por encima). La espera está ponderada por turnos, como la del encabezado.',
                columnas: ['Bloque', 'Turnos', 'Agendó el bot', 'Atendidos', 'Sobreturnos', 'Espera media (min)', 'Ocupación (%)'],
                filas: bloques.map((b) => [b.bloque, b.turnos, b.bot, b.atendidos, b.sobreturnos,
                    b.espera != null ? Math.round(b.espera) : null, Math.round(b.ocupacion)]),
            }),
            seccion({
                id: 'esperaSala', hoja: 'Espera en sala', titulo: 'Espera en sala por franja',
                nota: `Minutos entre la llegada y el inicio de la atención, solo con llegada registrada del mismo día: la misma definición de la Espera media del encabezado. Meta: hasta ${UMBRALES.esperaMin.bueno} min.`,
                columnas: ['Franja', 'Espera media (min)', 'Turnos medidos'],
                filas: espera.map((e) => [e.horario, r1(e.tiempoEsperaPromedioMinutos), e.turnosMedidos ?? null]),
                grafico: espera.length
                    ? graficoBarras(espera.map((e) => e.horario), [{
                        label: 'Espera media (min)',
                        data: espera.map((e) => r1(e.tiempoEsperaPromedioMinutos) ?? 0),
                        backgroundColor: espera.map((e) => colorEspera(r1(e.tiempoEsperaPromedioMinutos))),
                    }], { leyenda: false, sufijo: ' min' })
                    : null,
            }),
            seccion({
                id: 'mapaCalor', hoja: 'Mapa de calor', titulo: 'Demanda por día y hora',
                nota: 'Turnos pedidos en cada hora del día, por día de la semana.',
                columnas: ['Hora', ...diasConDatos.map((d) => NOMBRES_DIA[d])],
                filas: horasOrdenadas.map((hora) => [`${hora}:00`, ...diasConDatos.map((d) => porCelda.get(`${d}-${hora}`) || 0)]),
            }),
        ],
    };
}

function pestanaResultados(c) {
    const resumen = c.resumen || {};
    const conTramite = lista(c.conversionPorTipo).reduce((acc, x) => acc + (x.conTramite || 0), 0);
    const pasos = calcularPasosEmbudo({
        agendado: resumen.totalTurnos ?? 0, presentaron: resumen.turnosAtendidos ?? 0, conTramite,
    });
    const sinResolver = Math.max(0, (resumen.totalTurnos ?? 0) - (resumen.turnosAtendidos ?? 0)
        - (resumen.turnosNoAsistio ?? 0) - (resumen.turnosCancelados ?? 0));
    const ausentismo = [...lista(c.ausentismoPorCanal)].sort((a, b) => Number(b.tasaAusentismo || 0) - Number(a.tasaAusentismo || 0));
    const promedio = r1(resumen.tasaAusentismo);

    return {
        id: 'resultados',
        titulo: 'Resultados',
        secciones: [
            seccion({
                id: 'embudo', hoja: 'Embudo del turno', titulo: 'Embudo del turno',
                nota: `Dónde se pierde gente entre que se registra el turno y se abre un trámite. Los ${(resumen.totalTurnos ?? 0).toLocaleString('es-AR')} turnos del período: ${(resumen.turnosAtendidos ?? 0).toLocaleString('es-AR')} se presentaron, ${(resumen.turnosNoAsistio ?? 0).toLocaleString('es-AR')} no vinieron, ${(resumen.turnosCancelados ?? 0).toLocaleString('es-AR')} cancelaron${sinResolver > 0 ? ` y ${sinResolver.toLocaleString('es-AR')} siguen sin resolver` : ''}. No cuentan cobros, turnos médicos, ANSES ni quien vino por un trámite que ya tenía en curso.`,
                columnas: ['Paso', 'Cantidad', 'Se pierden respecto del paso anterior', 'Caída (%)'],
                filas: resumen.totalTurnos ? pasos.map((p) => [p.titulo, p.valor, p.perdida, r1(p.caidaPct)]) : [],
                grafico: resumen.totalTurnos
                    ? graficoBarras(pasos.map((p) => p.titulo), [{
                        label: 'Turnos', data: pasos.map((p) => p.valor),
                        backgroundColor: [COLOR.azulClaro, COLOR.azul, COLOR.verdeClaro, COLOR.verde],
                    }], { leyenda: false, alto: 380 })
                    : null,
            }),
            seccionResultadoAtencion('resultadoAtencion', 'Resultado de la atención', c.resultadoAtencion),
            seccionMotivosNoAvance('motivosNoAvance', 'Motivos de no avance', c.motivosNoAvance),
            seccion({
                id: 'ausentismoCanal', hoja: 'Ausentismo por canal', titulo: 'Ausentismo por canal de origen',
                nota: promedio != null ? `Promedio del estudio en el período: ${promedio.toLocaleString('es-AR')}%. En rojo, los canales por encima del promedio.` : null,
                columnas: ['Canal', 'Resueltos', 'No asistió', 'Ausentismo (%)'],
                filas: ausentismo.map((f) => [f.nombreCanal, f.resueltos ?? f.total, f.noAsistio, r1(f.tasaAusentismo)]),
                grafico: ausentismo.length
                    ? graficoBarras(ausentismo.map((f) => f.nombreCanal), [{
                        label: 'Ausentismo (%)', data: ausentismo.map((f) => r1(f.tasaAusentismo) ?? 0),
                        backgroundColor: ausentismo.map((f) => (promedio != null && Number(f.tasaAusentismo) > promedio ? COLOR.rojo : COLOR.azul)),
                    }], { horizontal: true, leyenda: false, sufijo: '%', alto: altoHorizontal(ausentismo.length) })
                    : null,
            }),
        ],
    };
}

function pestanaTendencias(c) {
    // El ausentismo del mes lo calcula el backend (turnos ya resueltos, la misma regla del
    // encabezado); acá no se recalcula con otros conteos. Un mes parcial va marcado con "*".
    const meses = lista(c.porMes).map((m) => ({
        ...m, label: `${etiquetaMes(m.mes, m.anio)}${m.parcial ? '*' : ''}`, ausentismo: r1(m.tasaAusentismo),
    }));
    const hayParciales = meses.some((m) => m.parcial);
    const conversion = lista(c.conversionMensual).map((m) => ({ ...m, label: etiquetaMes(m.mes, m.anio) }));
    const { meses: mesesTipo, series } = seriesTendenciaPorTipo(lista(c.tendenciaPorTipo));
    const dias = [...lista(c.porDia)].sort((a, b) => String(a.fecha).localeCompare(String(b.fecha)));

    return {
        id: 'tendencias',
        titulo: 'Tendencias',
        secciones: [
            seccion({
                id: 'comparativoMensual', hoja: 'Comparativo mensual', titulo: 'Comparativo mensual',
                nota: hayParciales
                    ? '* Mes parcial: el período lo corta o el sistema todavía no tenía turnos todo el mes. No se calcula la variación de turnos contra un mes parcial (compararlo inventa un cambio). El ausentismo sí: es una tasa.'
                    : null,
                columnas: ['Mes', 'Turnos', 'Atendidos', 'No asistió', 'Cancelados', 'Ausentismo (%)', 'Variación de turnos (%)'],
                filas: meses.map((m) => [m.label, m.total, m.atendidos, m.noAsistio, m.cancelados, m.ausentismo, r1(m.variacionTotalPct)]),
                grafico: meses.length
                    ? graficoLineas(meses.map((m) => m.label), [
                        { label: 'Turnos', data: meses.map((m) => m.total || 0), borderColor: COLOR.azul, backgroundColor: COLOR.azul },
                        { label: 'Atendidos', data: meses.map((m) => m.atendidos || 0), borderColor: COLOR.verde, backgroundColor: COLOR.verde },
                    ])
                    : null,
            }),
            seccion({
                id: 'conversionMensual', hoja: 'Conversión mensual', titulo: 'Conversión a trámite, mes a mes',
                columnas: ['Mes', 'Turnos', 'Con trámite abierto', 'Conversión (%)'],
                filas: conversion.map((m) => [m.label, m.totalTurnos, m.conTramite, r1(m.tasaConversion)]),
                grafico: conversion.length
                    ? graficoLineas(conversion.map((m) => m.label), [{
                        label: 'Conversión (%)', data: conversion.map((m) => r1(m.tasaConversion) ?? 0),
                        borderColor: COLOR.verde, backgroundColor: COLOR.verde,
                    }], { sufijo: '%' })
                    : null,
            }),
            seccion({
                id: 'tendenciaPorTipo', hoja: 'Tendencia por tipo', titulo: 'Tendencia del resultado de la atención',
                nota: `Un color por resultado cargado en Datos Maestros. Tipo de trámite: ${c.filtros?.tipoTramite || 'Todos'}.`,
                columnas: ['Mes', 'Atenciones', ...series.map((s) => s.etiqueta)],
                filas: mesesTipo.map((m, i) => [m.label, m.total, ...series.map((s) => s.valores[i])]),
                grafico: mesesTipo.length
                    ? graficoBarras(mesesTipo.map((m) => m.label),
                        series.map((s) => ({ label: s.etiqueta, data: s.valores, backgroundColor: s.color, stack: 'resultado' })),
                        { apilado: true })
                    : null,
            }),
            seccion({
                id: 'turnosPorDia', hoja: 'Turnos por día', titulo: 'Turnos por día',
                columnas: ['Día', 'Turnos', 'Atendidos'],
                filas: dias.map((d) => [fechaCorta(d.fecha), d.total, d.atendidos]),
                grafico: dias.length
                    ? graficoBarras(dias.map((d) => fechaCorta(d.fecha).substring(0, 5)), [
                        { label: 'Turnos', data: dias.map((d) => d.total || 0), backgroundColor: COLOR.azulClaro },
                        { label: 'Atendidos', data: dias.map((d) => d.atendidos || 0), backgroundColor: COLOR.verde },
                    ])
                    : null,
            }),
        ],
    };
}

function pestanaOperador(c) {
    const operadores = [...lista(c.porOperador)].sort((a, b) => (b.totalAtendidos || 0) - (a.totalAtendidos || 0));
    return {
        id: 'operador',
        titulo: 'Por operador',
        nota: `Período propio de esta sección: ${c.rangoOperador || 'Mes en curso'} (turnos finalizados).`,
        secciones: [
            seccion({
                id: 'operadores', hoja: 'Por operador', titulo: 'Rendimiento por operador',
                vacio: 'No hay turnos finalizados en el período de esta sección.',
                nota: 'La conversión de cada operador se calcula sobre su "Base de la conversión" (finalizados sin cobros ni quienes vinieron por un trámite que ya tenían en curso), no sobre todos sus finalizados.',
                columnas: ['Operador', 'Finalizados', 'Tiempo medio de atención (min)', 'Base de la conversión', 'Con trámite abierto', 'Conversión (%)'],
                filas: operadores.map((o) => [o.operador, o.totalAtendidos, r1(o.tiempoPromedioMinutos), o.baseConversion ?? null, o.conTramite, r1(o.tasaConversion)]),
                grafico: operadores.length
                    ? graficoBarras(operadores.map((o) => o.operador), [
                        { label: 'Finalizados', data: operadores.map((o) => o.totalAtendidos || 0), backgroundColor: COLOR.azul },
                        { label: 'Con trámite abierto', data: operadores.map((o) => o.conTramite || 0), backgroundColor: COLOR.verde },
                    ], { horizontal: true, alto: altoHorizontal(operadores.length * 2) })
                    : null,
            }),
        ],
    };
}

function pestanaHistoricas(c) {
    const h = c.historicas || {};
    const porTipo = [...lista(h.porTipo)].sort((a, b) => (b.cantidad || 0) - (a.cantidad || 0));
    return {
        id: 'historicas',
        titulo: 'Estadísticas históricas',
        nota: `Suma las atenciones de la planilla manual (mayo a agosto de 2026) a los turnos del sistema. Período propio de esta sección: ${c.rangoHistoricas || 'Año en curso'}. El filtro de área no se aplica: las atenciones históricas no tienen área registrada.`,
        secciones: [
            seccion({
                id: 'historicoTipo', hoja: 'Histórico - Por tipo', titulo: 'Atenciones por tipo de trámite',
                columnas: ['Tipo de trámite', 'Atenciones', 'Atendidos', 'Cancelados', '% del total'],
                filas: porTipo.map((f) => [c.formatearTipo(f.tipoTramite), f.cantidad, f.atendidos, f.cancelados, r1(f.porcentaje)]),
                grafico: porTipo.length
                    ? graficoBarras(porTipo.map((f) => c.formatearTipo(f.tipoTramite)),
                        [{ label: 'Atenciones', data: porTipo.map((f) => f.cantidad || 0), backgroundColor: COLOR.azul }], { leyenda: false })
                    : null,
            }),
            seccionResultadoAtencion('historicoResultado', 'Histórico - Resultado', h.resultadoAtencion),
            seccionMotivosNoAvance('historicoMotivos', 'Histórico - Motivos', h.motivosNoAvance),
        ],
    };
}

/**
 * @param {object} config ver datosParaExportar en ReportesRecepcion.js
 * @returns {{filtros: Array<[string, string]>, kpis: Array<[string, *]>, alertas: Array<{titulo, texto}>, pestanas: Array}}
 */
export function construirReporteRecepcion(config = {}) {
    const c = { ...config, formatearTipo: config.formatearTipo || ((t) => t || '—') };
    const resumen = c.resumen || {};

    const conversion = conversionGlobal(lista(c.conversionPorTipo));
    const finalizadas = lista(c.conversionPorTipo).reduce((acc, x) => acc + (x.totalTurnos || 0), 0);
    const cancelados = resumen.turnosCancelados ?? 0;
    const porBot = resumen.canceladosPorBot ?? 0;
    const delEstudio = resumen.canceladosManual ?? 0;

    const filtros = c.filtros || {};
    const reporte = {
        filtros: [
            ['Período', filtros.periodo || '—'],
            ['Tipo de trámite', filtros.tipoTramite || 'Todos'],
            ['Área de atención', filtros.area || 'Todas'],
            ['Canal de origen', filtros.canal || 'Todos'],
        ],
        // Sin resumen (la exportación de Estadísticas históricas) no hay indicadores que imprimir.
        kpis: !c.resumen ? [] : [
            ['Turnos del período', resumen.totalTurnos ?? 0],
            ['Se presentaron', resumen.turnosAtendidos ?? 0],
            ['Asistencia (%) — turnos ya resueltos (sin cancelados)', r1(resumen.tasaAsistencia)],
            ['Ausentismo (%) — complemento de la asistencia', r1(resumen.tasaAusentismo)],
            ['Espera media en sala (min) — promedio por turno, llegada del mismo día', resumen.esperaMediaMinutos != null ? Math.round(Number(resumen.esperaMediaMinutos)) : null],
            ['Atenciones finalizadas', finalizadas],
            ['Conversión a trámite (%) — sobre las atenciones finalizadas', r1(conversion)],
            ['Cancelaciones', cancelados],
            ['   pedidas por el BOT', porBot],
            ['   dadas de baja por el estudio', delEstudio],
            ['   sin origen registrado', Math.max(0, cancelados - porBot - delEstudio)],
            ['Turnos que agendó el bot (canal de creación)', resumen.turnosBot ?? 0],
            ['Turnos cargados en recepción', resumen.turnosManuales ?? 0],
            ['Sobreturnos', resumen.sobreturnos ?? 0],
            ['Tipo de trámite más pedido', resumen.tipoMasSolicitado ? c.formatearTipo(resumen.tipoMasSolicitado) : null],
            ['Horario pico', resumen.horarioPico || null],
        ],
        alertas: calcularInsights({
            resumen: c.resumen, porHorario: lista(c.porHorario), motivosNoAvance: lista(c.motivosNoAvance),
            ausentismoPorCanal: lista(c.ausentismoPorCanal), conversionPorTipo: lista(c.conversionPorTipo), capacidad: c.capacidad,
        }).map((i) => ({ titulo: i.title, texto: i.text, severidad: i.severity })),
        pestanas: [
            pestanaGeneral(c),
            pestanaOcupacion(c),
            pestanaResultados(c),
            pestanaTendencias(c),
            pestanaOperador(c),
            // Las históricas son una vista aparte: solo entran cuando la pantalla manda esos datos.
            ...(c.historicas ? [pestanaHistoricas(c)] : []),
        ],
    };
    return reporte;
}

/** Pestañas a incluir según la opción del modal de exportación ('todos' o el id de una pestaña). */
export function filtrarPestanas(reporte, seccionExportar = 'todos') {
    if (!seccionExportar || seccionExportar === 'todos') return reporte.pestanas;
    return reporte.pestanas.filter((p) => p.id === seccionExportar);
}

// Los usa también la exportación de la vista simple (vistaSimple.js).
export { graficoBarras, graficoDona };
